import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SearchIcon } from '@icons';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useRef } from 'react';
import { isArray, isDark } from '../common/utils';
import { Input } from '../input/input';
import { SidebarGroups } from './sidebar-groups';
import styles from './sidebar.module.scss';
const Sidebar = ({ border = false, enableSearch = false, focusable = true, groupIconColor, groupTitleColor, groups, height = '100%', listMaxHeight = 750, onSelect, searchPlaceholder = 'Search ...', sectionsCollapsible = true, icons, }) => {
    const [_groups, setGroups] = React.useState(isArray(groups)
        ? groups.map(item => ({
            ...item,
            id: nanoid(),
            items: item.items.map(obj => ({
                ...obj,
                id: nanoid(),
                selected: false,
            })),
            visible: true,
        }))
        : []);
    const [sidebarHeight, setSidebarHeight] = React.useState(0);
    const ref = useRef(null);
    const isDarkMode = useMemo(() => isDark(), []);
    const handleSelection = useCallback((option, groupId) => {
        if (option && groupId) {
            setGroups(prev => {
                return prev.map(item => ({
                    ...item,
                    items: item.items.map(item => ({
                        ...item,
                        selected: item.id === option[0].id,
                    })),
                }));
            });
            const grp = _groups.find(grp => grp.id === groupId);
            if (grp) {
                onSelect?.(grp, option[0]);
            }
        }
    }, []);
    const handleSearch = useCallback((ter) => {
        const tester = new RegExp(`\\b${ter.trim()}`, 'i');
        setGroups(prev => prev.map(group => {
            const visible = group.items.some(item => tester.test(item.name));
            return {
                ...group,
                items: group.items.map(item => ({
                    ...item,
                    visible: tester.test(item.name),
                })),
                visible,
            };
        }));
    }, [groups.length]);
    const sideBarClass = useMemo(() => classNames(styles.sidebar, {
        [styles.dark]: isDarkMode,
    }), [isDarkMode]);
    const style = useMemo(() => {
        return {
            '--sidebar-height': Number.isInteger(height) ? `${height}px` : height,
        };
    }, []);
    const contentWrapper = useMemo(() => {
        return classNames(styles.content_wrapper, {
            [styles.border]: border,
        });
    }, [border]);
    const onRef = useCallback((node) => {
        if (node) {
            ref.current = node;
            setSidebarHeight(node.clientHeight ? node.clientHeight : window.innerHeight);
        }
    }, []);
    return (_jsx("div", { className: sideBarClass, style: style, ref: onRef, children: _jsxs("div", { className: contentWrapper, children: [enableSearch && (_jsx("div", { className: styles.search_wrapper, children: _jsx(Input, { type: "text", enableClear: true, onChange: handleSearch, placeholder: searchPlaceholder, controlled: true, focusable: focusable, children: _jsx(SearchIcon, {}) }) })), _jsx(SidebarGroups, { groups: _groups, focusable: focusable, onSelection: handleSelection, groupIconColor: groupIconColor, groupTitleColor: groupTitleColor, sideBarHeight: sidebarHeight, listMaxHeight: listMaxHeight, sectionsCollapsible: sectionsCollapsible, icons: icons, enableSearch: enableSearch })] }) }));
};
Sidebar.displayName = 'Sidebar';
export { Sidebar };
